import { zodResolver } from "@hookform/resolvers/zod";
import { ChevronsRight } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";

import Box from "@/common/components/Box";
import Button from "@/common/components/Button";
import Checkbox from "@/common/components/Checkbox";
import { DateInput } from "@/common/components/DateInput/DateInput";
import { ErrorMessage } from "@/common/components/ErrorMessage";
import GoBackButton from "@/common/components/GoBackButton";
import { ModalMessageSimple } from "@/common/components/ModalMessageSimple";
import Select from "@/common/components/Select";
import TextArea from "@/common/components/TextArea";
import TextField from "@/common/components/TextField";
import { Option } from "@/common/types/option";
import { Mapper } from "@/common/services/mapper";
import { FormTransformers } from "@/common/utils/formTransformers";

import {
  missionMaxTimesOptions,
  missionTypeOptions,
} from "../constants/missionOptions.constant";
import { useMissionCompanyBranches } from "../hooks/useMissionCompanyBranches";
import { useMissionProducts } from "../hooks/useMissionProducts";
import { useMissionProviders } from "../hooks/useMissionProviders";
import { missionSchema, MissionSchema } from "../schemas/mission.schema";
import { MissionRewardFieldError } from "../types/rewardErrors";
import {
  Mission,
  MissionReward,
} from "../types/mission.type";
import { MissionProvider } from "../types/missionProvider.type";
import {
  MissionFormSubmitHelpers,
  RewardFormValue,
  buildMissionDefaultValues,
  extractMissionBackendErrorMessages,
  generateMissionFieldId,
  mapMissionRewardType,
  rewardTypeLabels,
  transformMissionBackendErrors,
} from "../services/missionFormHelpers";
import { RequirementsSection } from "./RequirementsSection";
import { RewardsSection } from "./RewardsSection";
import { MissionProvidersTransformer } from "../services/missionProvidersTransformer";
import { MissionFormOptionsTransformer } from "../services/missionFormOptionsTransformer";
export type { MissionFormSubmitHelpers };

type MissionFormProps = {
  mode: "create" | "edit";
  initialValues?: Mission;
  onSubmit: (values: MissionSchema, helpers: MissionFormSubmitHelpers) => void;
  isSubmitting?: boolean;
  onCancel: () => void;
};

export const MissionForm = ({
  mode,
  initialValues,
  onSubmit,
  isSubmitting = false,
  onCancel,
}: MissionFormProps) => {
  const submitPermission =
    mode === "create"
      ? "Admin::Tmais::Missions::Create"
      : "Admin::Tmais::Missions::Update";

  const form = useForm<MissionSchema>({
    resolver: zodResolver(missionSchema),
    defaultValues: buildMissionDefaultValues(initialValues),
  });

  const {
    control,
    register,
    handleSubmit,
    watch,
    setValue,
    clearErrors,
    setError: setFormError,
    reset,
    formState: { errors },
  } = form;

  const [messages, setMessages] = useState<string[] | boolean>(false);
  const [manualRewardsError, setManualRewardsError] = useState<string | null>(
    null,
  );
  const [establishmentSearch, setEstablishmentSearch] = useState("");

  const watchMissionType = watch("mission_type");
  const currentEstablishmentName = watch("establishment")?.display_name || "--";
  const watchAllowMultipleTimes = watch("allow_multiple_times");
  const conditions = watch("conditions") || [];
  const rewards = (watch("rewards") || []) as RewardFormValue[];

  const {
    data: providerData = [],
    dataUpdatedAt: providerDataUpdatedAt,
  } = useMissionProviders<MissionProvider[]>({
    mapper: Mapper.mapWithTransformer({
      transformer: MissionProvidersTransformer.normalizeProviders,
    }),
  });
  const providerList: MissionProvider[] = Array.isArray(providerData)
    ? (providerData as MissionProvider[])
    : [];
  const {
    data: productData = [],
    dataUpdatedAt: productDataUpdatedAt,
  } = useMissionProducts();

  const initialRewardsSignature =
    initialValues?.rewards && initialValues.rewards.length > 0
      ? `${initialValues.rewards
          .map(
            (reward) =>
              `${reward.id || reward.reward_id || reward.product_id || ""}-${
                reward.reward_type
              }`,
          )
          .join("|")}`
      : "no-rewards";
  const initialEstablishmentSignature = initialValues?.establishment
    ? `${initialValues.establishment.id}-${initialValues.establishment.display_name}`
    : "no-establishment";

  const providerOptions = useMemo<Option[]>(() => {
    return MissionFormOptionsTransformer.providerOptions(providerList);
  }, [providerDataUpdatedAt]);

  const fetchedProductOptions = useMemo<Option[]>(() => {
    return MissionFormOptionsTransformer.productOptionsFromApi(productData);
  }, [productDataUpdatedAt]);

  const initialProductOptions = useMemo<Option[]>(() => {
    return MissionFormOptionsTransformer.initialProductOptions(initialValues);
  }, [initialRewardsSignature]);

  const productOptions = useMemo<Option[]>(() => {
    return MissionFormOptionsTransformer.mergeOptions(
      initialProductOptions,
      fetchedProductOptions,
    );
  }, [initialRewardsSignature, productDataUpdatedAt]);

  const initialEstablishmentOptions = useMemo<Option[]>(() => {
    return MissionFormOptionsTransformer.initialEstablishmentOptions(
      initialValues,
    );
  }, [initialEstablishmentSignature]);

  const shouldFetchEstablishments = watchMissionType === "establishment";

  const {
    data: fetchedBranches = [],
    dataUpdatedAt: branchesUpdatedAt,
    isFetching: isFetchingEstablishments,
  } = useMissionCompanyBranches({
      search: establishmentSearch,
      enabled: shouldFetchEstablishments,
    });

  const fetchedEstablishmentOptions = useMemo<Option[]>(() => {
    return MissionFormOptionsTransformer.establishmentOptionsFromBranches(
      fetchedBranches,
    );
  }, [branchesUpdatedAt]);

  const establishmentOptions = useMemo<Option[]>(() => {
    return MissionFormOptionsTransformer.mergeOptions(
      initialEstablishmentOptions,
      fetchedEstablishmentOptions,
    );
  }, [initialEstablishmentSignature, branchesUpdatedAt]);

  useEffect(() => {
    reset(buildMissionDefaultValues(initialValues));
    setManualRewardsError(null);
  }, [initialValues, reset]);

  useEffect(() => {
    if (watchMissionType === "tcard") {
      setValue("establishment", null);
    }
  }, [watchMissionType, setValue]);

  useEffect(() => {
    if (watchMissionType !== "establishment") {
      setEstablishmentSearch("");
    }
  }, [watchMissionType]);

  useEffect(() => {
    if (!productOptions.length || !rewards.length) return;

    const rewardsMissingProduct = rewards.some(
      (reward) => reward.reward_type === "product" && !reward.reward_id,
    );

    if (!rewardsMissingProduct) return;

    const assignedProductIds = new Set(
      rewards
        .filter(
          (reward) => reward.reward_type === "product" && reward.reward_id,
        )
        .map((reward) => String(reward.reward_id)),
    );

    const pickNextProduct = () => {
      for (const option of productOptions) {
        const value = String(option.value);
        if (!assignedProductIds.has(value)) {
          assignedProductIds.add(value);
          return option;
        }
      }
      return null;
    };

    let hasChanges = false;
    const updatedRewards = rewards.map((reward) => {
      if (reward.reward_type === "product" && !reward.reward_id) {
        const nextProduct = pickNextProduct();
        if (!nextProduct) return reward;
        hasChanges = true;
        return {
          ...reward,
          reward_id: String(nextProduct.value),
          product_id: String(nextProduct.value),
          reward_label: String(nextProduct.label),
          product_name: String(nextProduct.label),
        };
      }

      return reward;
    });

    if (hasChanges) {
      setValue("rewards", updatedRewards, { shouldDirty: true });
      setManualRewardsError(null);
    }
  }, [productOptions, rewards, setValue]);

  const handleFormSubmit = (values: MissionSchema) => {
    const handleTransformedErrors =
      FormTransformers.errorsTransformer<MissionSchema>({
        setError: setFormError,
        transformer: transformMissionBackendErrors,
      });

    const payload: MissionSchema = {
      ...values,
      max_times: values.max_times ?? null,
      establishment:
        values.mission_type === "tcard" ? null : values.establishment,
      rewards: values.rewards,
    };

    if (!payload.rewards.length) {
      setManualRewardsError(
        "Adicione pelo menos uma recompensa antes de salvar.",
      );
      return;
    }

    const productRewards = payload.rewards.filter(
      (reward) => reward.reward_type === "product",
    );

    const hasMissingRewardId = productRewards.some(
      (reward) => !reward.reward_id,
    );

    if (hasMissingRewardId) {
      setManualRewardsError(
        "Selecione uma recompensa válida ou remova os itens sem identificação.",
      );
      return;
    }

    const productRewardIds = productRewards
      .filter((reward) => reward.reward_id)
      .map((reward) => String(reward.reward_id));

    const hasRepeatedProduct =
      new Set(productRewardIds).size !== productRewardIds.length;

    if (hasRepeatedProduct) {
      setManualRewardsError(
        "Não é possível cadastrar recompensas com o mesmo produto.",
      );
      return;
    }

    setManualRewardsError(null);
    onSubmit(payload, {
      onError: (error) => {
        const backendMessages = extractMissionBackendErrorMessages(error);
        if (backendMessages.length) {
          setMessages(backendMessages);
        }
        handleTransformedErrors(error);
      },
    });
  };

  const handleAddCondition = (
    condition: Omit<Mission["conditions"][number], "id">,
  ) => {
    setValue(
      "conditions",
      [...conditions, { ...condition, id: generateMissionFieldId() }],
      {
        shouldDirty: true,
      },
    );
    clearErrors("conditions");
  };

  const handleRemoveCondition = (id: string) => {
    setValue(
      "conditions",
      conditions.filter((condition) => condition.id !== id),
      { shouldDirty: true },
    );
    clearErrors("conditions");
  };

  const handleUpdateCondition = (
    id: string,
    data: Partial<Mission["conditions"][number]>,
  ) => {
    setValue(
      "conditions",
      conditions.map((condition) =>
        condition.id === id ? { ...condition, ...data } : condition,
      ),
      { shouldDirty: true },
    );
    clearErrors("conditions");
  };

  const handleAddReward = (payload?: {
    reward_id?: string;
    reward_label?: string;
    product_name?: string;
    quantity?: number;
    reward_type?: MissionReward["reward_type"];
  }) => {
    const resolvedType = mapMissionRewardType(payload?.reward_type);
    const baseReward: RewardFormValue = {
      id: generateMissionFieldId(),
      reward_type: resolvedType,
      quantity: payload?.quantity ?? 1,
      reward_id: payload?.reward_id,
      reward_label: payload?.reward_label ?? undefined,
      product_name: payload?.product_name ?? undefined,
      product_id: payload?.reward_id,
    };

    if (baseReward.reward_type === "product") {
      if (!baseReward.reward_id) {
        const defaultProduct = productOptions[0];

        if (defaultProduct) {
          baseReward.reward_id = String(defaultProduct.value);
          baseReward.product_id = String(defaultProduct.value);
          baseReward.reward_label =
            payload?.reward_label ?? String(defaultProduct.label);
          baseReward.product_name =
            payload?.product_name ?? String(defaultProduct.label);
        }
      }
    } else {
      baseReward.reward_id = undefined;
      baseReward.product_id = undefined;
      baseReward.product_name = undefined;
      baseReward.reward_label =
        payload?.reward_label ??
        rewardTypeLabels[baseReward.reward_type] ??
        "Pontos";
    }

    setValue("rewards", [...rewards, baseReward], { shouldDirty: true });
    setManualRewardsError(null);
  };

  const handleRemoveReward = (id: string) => {
    setValue(
      "rewards",
      rewards.filter((reward) => reward.id !== id),
      { shouldDirty: true },
    );
    setManualRewardsError(null);
  };

  const handleUpdateReward = (id: string, data: Partial<RewardFormValue>) => {
    const currentReward = rewards.find((reward) => reward.id === id);
    const defaultProduct = productOptions[0];
    const normalized: Partial<RewardFormValue> = { ...data };

    if ("reward_type" in data && data.reward_type) {
      const resolvedType = mapMissionRewardType(data.reward_type);
      normalized.reward_type = resolvedType;
      if (resolvedType !== "product") {
        normalized.reward_id = undefined;
        normalized.product_id = undefined;
        normalized.product_name = undefined;
        normalized.reward_label =
          data.reward_label ??
          rewardTypeLabels[resolvedType] ??
          "Pontos";
      } else {
        const fallbackProduct =
          (currentReward?.reward_id &&
            productOptions.find(
              (option) =>
                String(option.value) === String(currentReward.reward_id),
            )) ||
          defaultProduct;

        if (fallbackProduct) {
          normalized.reward_id = String(fallbackProduct.value);
          normalized.product_id = String(fallbackProduct.value);
          normalized.reward_label = String(fallbackProduct.label);
          normalized.product_name = String(fallbackProduct.label);
        }
      }
    }

    if ("reward_id" in data && data.reward_id) {
      const selectedProduct = productOptions.find(
        (option) => String(option.value) === String(data.reward_id),
      );

      normalized.product_id = String(data.reward_id);
      normalized.reward_label = selectedProduct
        ? String(selectedProduct.label)
        : normalized.reward_label;
      normalized.product_name = selectedProduct
        ? String(selectedProduct.label)
        : normalized.product_name;
    }

    setValue(
      "rewards",
      rewards.map((reward) =>
        reward.id === id ? { ...reward, ...normalized } : reward,
      ),
      { shouldDirty: true },
    );

    if (
      ("reward_id" in normalized && normalized.reward_id) ||
      ("reward_type" in normalized && normalized.reward_type !== "product")
    ) {
      setManualRewardsError(null);
    }
  };
  const rewardFieldErrors = Array.isArray(errors.rewards)
    ? (errors.rewards as MissionRewardFieldError[])
    : undefined;

  const rewardsListErrorFromSchema = Array.isArray(errors.rewards)
    ? undefined
    : errors.rewards?.message;

  const combinedRewardsListError =
    manualRewardsError || rewardsListErrorFromSchema;

  const conditionsFieldError = errors.conditions;
  const conditionsErrorMessage =
    conditionsFieldError && !Array.isArray(conditionsFieldError)
      ? (conditionsFieldError as { message?: string }).message
      : undefined;

  return (
    <form
      onSubmit={handleSubmit(handleFormSubmit)}
      className="flex w-full flex-col gap-6"
    >
      <div className="mb-6 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <GoBackButton fallback="/tmais-missions" />
          <p className="font-normal text-gray-400">Missões</p>
          <ChevronsRight className="h-6 w-6 text-gray-400" />
          <h2 className="font-bold">
            {mode === "create" ? "Cadastrar missão" : "Editar missão"}
          </h2>
        </div>

        <div className="flex gap-4">
          <Button
            type="button"
            variant="outline-error"
            onClick={onCancel}
            disabled={isSubmitting}
          >
            Cancelar
          </Button>
          <Button
            type="submit"
            className="shadow-[4px_4px_8px_4px_rgba(0,_0,_0,_0.2)]"
            operationKlassToCheckPermission={submitPermission}
            isLoading={isSubmitting}
            disabled={isSubmitting}
          >
            {mode === "create" ? "Cadastrar missão" : "Salvar alterações"}
          </Button>
        </div>
      </div>

      <Box className="flex flex-col gap-6 shadow-[4px_4px_8px_4px_rgba(0,_0,_0,_0.2)]">
        <p className="mb-2 text-xl font-bold">Detalhes da Missão</p>

        <div className="flex flex-col gap-4 md:flex-row md:items-start md:gap-6">
          <div className="flex flex-col gap-2 md:min-w-[200px]">
            <span className="text-sm text-gray-400">Tipo de missão</span>
            <div className="flex flex-wrap gap-4">
              {missionTypeOptions.map((option) => {
                const isActive = watchMissionType === option.value;
                const description =
                  option.value === "establishment"
                    ? "(A missão será criada para o estabelecimento selecionado)"
                    : "";

                return (
                  <label
                    key={option.value}
                    className="flex items-center gap-2 text-sm text-gray-300"
                  >
                    <span
                      className={`flex h-4 w-4 items-center justify-center rounded-full border ${
                        isActive ? "border-success" : "border-gray-500"
                      }`}
                    >
                      <input
                        type="radio"
                        className="hidden"
                        name="mission_type"
                        value={option.value}
                        checked={isActive}
                        onChange={() =>
                          setValue(
                            "mission_type",
                            option.value as Mission["mission_type"],
                            {
                              shouldDirty: true,
                            },
                          )
                        }
                        disabled={isSubmitting}
                      />
                      {isActive && (
                        <span className="h-2 w-2 rounded-full bg-success" />
                      )}
                    </span>
                    <span>
                      {option.label}{" "}
                      {description && (
                        <span className="text-xs text-gray-400">
                          {description}
                        </span>
                      )}
                    </span>
                  </label>
                );
              })}
            </div>
          </div>

          {mode !== "create" && (
            <div className="flex flex-col gap-1 md:min-w-[200px]">
              <span className="text-sm text-gray-400">Status</span>
              <span className="text-base font-semibold text-white">
                {initialValues?.status === "inactive" ? "Inativa" : "Ativa"}
              </span>
            </div>
          )}

          {mode === "edit" && watchMissionType === "establishment" && (
            <div className="flex flex-col gap-1 md:flex-1">
              <span className="text-sm text-gray-400">Estabelecimento</span>
              <span className="text-base font-semibold text-white">
                {currentEstablishmentName}
              </span>
            </div>
          )}
        </div>

        <input type="hidden" {...register("status")} />

        {watchMissionType === "establishment" && (
          <>
            <Controller
              control={control}
              name="establishment"
              render={({ field }) => (
                <Select
                  label="Estabelecimento"
                  markAsRequired
                  placeholder="Selecione"
                  options={establishmentOptions}
                  value={
                    field.value
                      ? establishmentOptions.find(
                          (option) =>
                            String(option.value) === String(field.value?.id),
                        ) || {
                          label: field.value.display_name,
                          value: field.value.id,
                        }
                      : null
                  }
                  onChange={(option) => {
                    const normalized = option as Option | null;
                    field.onChange(
                      normalized
                        ? {
                            id: String(normalized.value),
                            display_name: String(normalized.label),
                          }
                        : null,
                    );
                  }}
                  onInputChange={(inputValue, meta) => {
                    if (meta.action === "input-change") {
                      setEstablishmentSearch(inputValue);
                    }
                    return inputValue;
                  }}
                  isDisabled={isSubmitting}
                  isSearchable
                  isLoading={isFetchingEstablishments}
                />
              )}
            />
            {errors.establishment?.message && (
              <ErrorMessage
                name="establishment"
                message={errors.establishment.message as string}
              />
            )}
          </>
        )}

        <TextField
          label="Nome"
          markAsRequired
          errorMessage={errors.name?.message}
          disabled={isSubmitting}
          {...register("name")}
        />

        <TextArea
          label="Descrição"
          markAsRequired
          errorMessage={errors.description?.message}
          disabled={isSubmitting}
          className="resize-none"
          rows={3}
          {...register("description")}
        />

        <div className="mt-2 flex flex-col gap-4">
          <Checkbox
            label="Permitir que missão possa ser feita múltiplas vezes (a cada realização, os requisitos serão resetados)"
            className="text-gray-300"
            {...register("allow_multiple_times")}
            disabled={isSubmitting}
          />

          {watchAllowMultipleTimes && (
            <Controller
              control={control}
              name="max_times"
              render={({ field }) => (
                <Select
                  label="Máximo de vezes"
                  markAsRequired
                  options={missionMaxTimesOptions}
                  value={
                    field.value
                      ? missionMaxTimesOptions.find(
                          (option) => option.value === String(field.value),
                        ) || null
                      : null
                  }
                  onChange={(option) =>
                    field.onChange(
                      option ? Number((option as Option).value) : null,
                    )
                  }
                  isDisabled={isSubmitting}
                />
              )}
            />
          )}
        </div>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <DateInput
            fieldName="start_date"
            control={control as never}
            errors={errors as never}
            label="Data inicial da missão"
            markAsRequired
          />
          <DateInput
            fieldName="end_date"
            control={control as never}
            errors={errors as never}
            label="Data final da missão"
            markAsRequired
          />
        </div>
      </Box>

      <RequirementsSection
        conditions={conditions}
        onAddCondition={handleAddCondition}
        onRemoveCondition={handleRemoveCondition}
        onUpdateCondition={handleUpdateCondition}
        isSubmitting={isSubmitting}
        conditionTypeOptions={providerOptions}
        errorMessage={conditionsErrorMessage}
      />

      <RewardsSection
        rewards={rewards}
        onAddReward={handleAddReward}
        onUpdateReward={handleUpdateReward}
        onRemoveReward={handleRemoveReward}
        isSubmitting={isSubmitting}
        rewardErrors={rewardFieldErrors}
        rewardsListError={combinedRewardsListError}
        productOptions={productOptions}
        missionType={watchMissionType}
      />

      {messages && (
        <ModalMessageSimple
          messages={messages as string[]}
          setMessages={setMessages}
          isError
        />
      )}
    </form>
  );
};
